import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as ProfileIcon } from '../../assets/profileDropdown.svg';
import { ReactComponent as HistoryIcon } from '../../assets/PurchaseHistoryDropdown.svg';
import { ReactComponent as LibraryIcon } from '../../assets/MyLibraryDropdown.svg';
import { ReactComponent as LogoutIcon } from '../../assets/logoutDropdown.svg';
import { useAppContext } from '../../AppContext';
import { showErrorToast } from '../Toastify/toastHelper';
import i18n from '../../locales/i18n';
import { useTranslation } from 'react-i18next';
import LogoutConfirmationModal from './LogoutConfirmationModal';
/**
 * Interface for the navigation items in the dropdown
 */
interface NavigationItem {
    id: string;
    label: string;
    path: string;
    icon: React.ReactNode;
    isVisible: boolean;
}

/**
 * Props interface for the ProfileDropdown component
 */
interface ProfileDropdownProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

/**
 * ProfileDropdown component that displays user navigation options and sign out functionality
 * @param {ProfileDropdownProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ isOpen, setIsOpen }) => {
    const navigate = useNavigate();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const { role, logout, setShowLogoutModal } = useAppContext();
    const [isSigningOut, setIsSigningOut] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isRTL, setIsRTL] = useState<boolean>(i18n.language === 'ar');
    const { setShowModal: setChangePasswordModal, setModalMode } = useAppContext();

    const {t} = useTranslation();
    useEffect(() => {
        const handleLanguageChange = () => {
            setIsRTL(i18n.language === 'ar');
        };
        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);
    // Navigation items configuration
    const navigationItems: NavigationItem[] = [
        {
            id: 'profile',
            label: t('navigation.profile'),
            path: '/profile?page=Profile',
            icon: <ProfileIcon aria-hidden="true" />,
            isVisible: role !== 'publisher'
        },
        {
            id: 'library',
            label: t('navigation.myLibrary'),
            path: '/profile?page=My Library',
            icon: <LibraryIcon aria-hidden="true" />,
            isVisible: role !== 'publisher'
        },
        {
            id: 'history',
            label: t('navigation.purchaseHistory'),
            path: '/profile?page=Purchase History',
            icon: <HistoryIcon aria-hidden="true" />,
            isVisible: role !== 'publisher'
        },
        {
            id: 'changePassword',
            label: t('profile.changePassword'),
            path: '/profile?page=Change Password',
            icon: null,
            isVisible: role === 'publisher'
        }
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Don't close if clicking on the modal
            if ((event.target as HTMLElement).closest('.modal-overlay')) {
                return;
            }
            
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, setIsOpen]);

    // Add effect to reset logout modal when dropdown is closed
    // useEffect(() => {
    //     if (!isOpen) {
    //         setShowLogoutModal(false);
    //     }
    // }, [isOpen]);

    const handleSignOut = () => {
        setShowLogoutModal(true);
        // console.log('Opening logout modal');
        // setIsOpen(false);
    };

    const handleNavigation = (path: string, itemId?: string) => {
        if (itemId === 'changePassword') {
            setChangePasswordModal(true);
            setModalMode('forgotPassword');
            setIsOpen(false);
        } else {
            navigate(path);
            setIsOpen(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div 
                className="relative" 
                ref={dropdownRef}
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="user-menu-button"
            >
                <div 
                    className={`absolute w-[245px] right-0 mt-2 w-fit bg-white rounded-[17px] border-[1px] border-[#D5D5D5] shadow-lg py-1 z-50`}
                >
                    {navigationItems.map((item) => (
                        item.isVisible && (
                            <React.Fragment key={item.id}>
                                <button
                                    onClick={() => handleNavigation(item.path, item.id)}
                                    className={`block w-full flex items-center gap-[8px] text-left text-[#636363] font-medium text-[20px] ${!isRTL ? 'pl-[24px] py-[16px] pr-[8px]' : 'px-[24px] py-[16px]'} text-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-nowrap`}
                                    role="menuitem"
                                >
                                    {item.icon}
                                    {item.label}
                                </button>
                                <div className='w-full h-[1px] bg-[#D5D5D5]' />
                            </React.Fragment>
                        )
                    ))}
                    
                    <button
                        onClick={handleSignOut}
                        disabled={isSigningOut}
                        className="block w-full text-nowrap min-w-[180px] flex items-center gap-[8px] text-left px-[24px] py-[16px] text-sm text-[#BE1E2D] font-medium text-[20px] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        role="menuitem"
                    >   
                        <LogoutIcon aria-hidden="true" />
                        {isSigningOut ? t('navigation.signingOut') : t('navigation.signOut')}
                    </button>
                    
                    {error && (
                        <div className="px-[24px] py-[8px] text-sm text-red-600" role="alert">
                            {error}
                        </div>
                    )}
                </div>
            </div>

        </>
    );
};

export default ProfileDropdown; 